import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Users, LayersIcon, FileText, Megaphone,
  MessageSquare, Target, Sparkles, BookOpen, Settings2,
  UsersRound, Shield, LogOut, Menu, X, Bell, Zap, ShieldCheck, SearchCheck, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import NotificationsMenu from "@/components/NotificationsMenu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inbox", label: "Inbox", icon: MessageSquare, testid: "nav-inbox" },
  { to: "/qualified", label: "Qualified Leads", icon: Target, testid: "nav-qualified" },
  { to: "/leads", label: "Leads CRM", icon: Users, testid: "nav-leads" },
  { to: "/follow-ups", label: "Follow-ups", icon: BookOpen, testid: "nav-followups" },
  { to: "/lead-finder", label: "Lead Finder", icon: SearchCheck, testid: "nav-lead-finder" },
  { to: "/keyword-research", label: "Keyword Research", icon: TrendingUp, testid: "nav-keyword-research" },
  { to: "/contacts", label: "Contacts", icon: UsersRound, testid: "nav-contacts" },
  { to: "/segments", label: "Segments", icon: LayersIcon, testid: "nav-segments" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns" },
  { to: "/templates", label: "Templates", icon: FileText, testid: "nav-templates" },
  { to: "/ai-agent", label: "AI Agent", icon: Sparkles, testid: "nav-ai-agent" },
  { to: "/knowledge", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/whatsapp", label: "WhatsApp", icon: Settings2, testid: "nav-whatsapp" },
  { to: "/team", label: "Sales Associates", icon: Users, testid: "nav-team" },
];

function SidebarInner({ user, company, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="px-5 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[15px] font-bold text-slate-900 leading-tight">WaggleAI</div>
            <div className="text-[11px] text-slate-500 leading-tight">{company?.name || "Workspace"}</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 space-y-0.5">
        {NAV.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`
            }
          >
            <it.icon className="w-4 h-4 shrink-0" />
            <span>{it.label}</span>
          </NavLink>
        ))}
        {user?.role === "super_admin" && (
          <NavLink to="/super-admin" data-testid="nav-super-admin" onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
                isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"
              }`}>
            <Shield className="w-4 h-4" /> Super Admin
          </NavLink>
        )}
      </nav>
      <div className="p-3 border-t border-slate-100">
        <a href="/waggleai-user-manual.pdf" target="_blank" rel="noopener noreferrer"
           data-testid="user-manual-link"
           className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-50 hover:text-emerald-700 transition-colors mb-1">
          <FileText className="w-3.5 h-3.5" />
          <span>User manual (PDF)</span>
        </a>
        <NavLink to="/profile" onClick={onNavigate} data-testid="nav-profile"
          className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-50 group">
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-semibold">
              {(user?.name || "U").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate group-hover:text-emerald-700">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate capitalize">{user?.role?.replace("_", " ")}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={(e)=>{e.preventDefault();e.stopPropagation();onLogout();}} data-testid="logout-button" className="text-slate-500 hover:text-rose-600">
            <LogOut className="w-4 h-4" />
          </Button>
        </NavLink>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, company, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [wa, setWa] = useState(null);
  useEffect(() => {
    const load = () => api.get("/whatsapp/connection").then(r => setWa(r.data)).catch(()=>{});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  const isLive = wa?.mode === "live";

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0">
        <SidebarInner user={user} company={company} onLogout={logout} />
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-slate-200 px-3 h-14 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SidebarInner user={user} company={company} onLogout={logout} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white">
                <Zap className="w-4 h-4" />
              </div>
              <div className="text-sm font-bold text-slate-900">WaggleAI</div>
            </div>
          </div>
          <NotificationsMenu />
        </header>

        {/* Desktop top bar */}
        <header className="hidden lg:flex bg-white border-b border-slate-200 h-14 items-center justify-between px-6 sticky top-0 z-30">
          <div className="text-sm text-slate-600">
            {isLive ? (
              <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-800 font-medium" data-testid="live-mode-badge">
                <ShieldCheck className="w-3 h-3 mr-1"/>LIVE MODE • Meta WhatsApp Cloud API
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-800 font-medium" data-testid="mock-mode-badge">
                MOCK MODE • Simulated WhatsApp API
              </Badge>
            )}
          </div>
          <NotificationsMenu />
        </header>

        <main className="flex-1 overflow-auto scrollbar-thin">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
